import { formatDistanceToNow } from "date-fns";
import {
  Calendar,
  Clock,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
} from "lucide-react";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

import type { Call } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

type CallCardProps = Call & BaseEntityCardProps;

// Avatar component for CallCard
export function CallCardAvatar({ className, style }: Partial<CallCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Phone className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function CallCard({
  platform,
  title,
  body = "",
  timestamp,
  duration,
  status,
  direction,
  created_at,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: CallCardProps) {
  const formattedCreatedDate = created_at
    ? formatDistanceToNow(new Date(created_at), { addSuffix: true })
    : "";

  const formattedCallDate = timestamp
    ? formatDistanceToNow(new Date(timestamp), { addSuffix: true })
    : "";

  const bodyPreview =
    body && body.length > 100 ? `${body.substring(0, 100)}...` : body;

  // Format duration from seconds to readable format
  const formatDuration = (seconds?: number) => {
    if (!seconds) return "";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  // Map status to color and variant
  const getStatusDetails = (status?: string) => {
    if (!status)
      return {
        color: "bg-neutral-100 text-neutral-800",
        variant: "outline" as const,
      };

    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes("completed")) {
      return {
        color: "bg-spectrum-green-light/30 text-green-800",
        variant: "outline" as const,
      };
    } else if (lowerStatus.includes("scheduled")) {
      return {
        color: "bg-spectrum-blue-light/30 text-teal",
        variant: "secondary" as const,
      };
    } else if (lowerStatus.includes("attempted")) {
      return {
        color: "bg-amber-100 text-amber-800",
        variant: "outline" as const,
      };
    } else if (lowerStatus.includes("canceled")) {
      return {
        color: "bg-red-100 text-red-800",
        variant: "outline" as const,
      };
    }
    return {
      color: "bg-neutral-100 text-neutral-800",
      variant: "outline" as const,
    };
  };

  // Map direction to color and icon
  const getDirectionDetails = (direction?: string) => {
    if (!direction)
      return {
        color: "bg-neutral-100 text-neutral-800",
        icon: Phone,
        variant: "outline" as const,
      };

    const lowerDirection = direction.toLowerCase();
    if (lowerDirection === "inbound") {
      return {
        color: "bg-spectrum-green-light/30 text-green-800",
        icon: PhoneIncoming,
        variant: "outline" as const,
      };
    } else if (lowerDirection === "outbound") {
      return {
        color: "bg-spectrum-blue-light/30 text-teal",
        icon: PhoneOutgoing,
        variant: "outline" as const,
      };
    }
    return {
      color: "bg-neutral-100 text-neutral-800",
      icon: Phone,
      variant: "outline" as const,
    };
  };

  // Format status text to capital case
  const formatStatus = (status?: string) => {
    if (!status) return "";
    return status.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Format direction text to capital case
  const formatDirection = (direction?: string) => {
    if (!direction) return "";
    return direction.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const statusDetails = getStatusDetails(status);
  const directionDetails = getDirectionDetails(direction);
  const DirectionIcon = directionDetails.icon;

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <CallCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{title}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {web_link && (
              <BaseEntityCardExternalLinkAction
                href={web_link}
                tooltip="Open call details"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {bodyPreview && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <BaseEntityCardDescription className="text-sm text-foreground line-clamp-2">
                    {bodyPreview}
                  </BaseEntityCardDescription>
                </TooltipTrigger>
                <TooltipContent className="max-w-md">
                  <p
                    className="text-sm"
                    style={{
                      wordBreak: "break-word",
                    }}
                  >
                    {body}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <BaseEntityCardTags>
            {status && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant={statusDetails.variant}
                      className={`text-xs ${statusDetails.color}`}
                    >
                      {formatStatus(status)}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Call status</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {direction && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant={directionDetails.variant}
                      className={`text-xs ${directionDetails.color} flex items-center gap-1`}
                    >
                      <DirectionIcon className="h-3 w-3" />
                      {formatDirection(direction)}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Call direction</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {duration && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{formatDuration(duration)}</span>
              </div>
            )}
          </BaseEntityCardTags>

          {(formattedCallDate || formattedCreatedDate) && (
            <BaseEntityCardMetadata>
              {formattedCallDate && (
                <BaseEntityCardMetadataItem
                  icon={<Calendar className="h-3 w-3" />}
                >
                  Called {formattedCallDate}
                </BaseEntityCardMetadataItem>
              )}

              {formattedCreatedDate &&
                formattedCreatedDate !== formattedCallDate && (
                  <>
                    {formattedCallDate && (
                      <span className="text-xs text-muted-foreground">•</span>
                    )}
                    <span>Created {formattedCreatedDate}</span>
                  </>
                )}
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function CallCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={className} style={style}>
      <Skeleton className="h-10 w-10 rounded-full" />
      <BaseEntityCardContent>
        <div className="flex items-center justify-between gap-2 mb-1">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-4" />
        </div>
        <Skeleton className="h-4 w-full mb-1.5" />
        <Skeleton className="h-4 w-3/4 mb-1.5" />
        <div className="flex gap-2 items-center mb-1.5">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-14" />
          <Skeleton className="h-5 w-12" />
        </div>
        <Skeleton className="h-3 w-1/2" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
